import { CustomBottomBar, CustomPageTitle, MinimalStepper } from '@components';
import { Button, Checkbox, Container, Typography, Icon, Dropdown, Textarea } from '@design-system';
import { useCreateTestsheetsPageVM } from './useCreateTestsheetsPageVM';
import './CreateTestsheetsPage.scss';
import { Answer, TestsheetQuestion } from '@services';
import { LiaArrowLeftSolid } from 'react-icons/lia';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { HiMiniDocumentMagnifyingGlass } from 'react-icons/hi2';

export const CreateTestsheetsPage: React.FC = () => {
    const {
        form,
        computedStepperSteps,
        currentStep,
        selectAnswer,
        selectedAnswers,
        questionComments,
        setQuestionComment,
        observations,
        setObservations,
        canGoNext,
        canGoPrev,
        isLastStep,
        handleNextStep,
        handlePrevStep,
        handleFinish,
        isCreating,
        operatorsOptions,
        worksitesOptions,
        apartmentsOptions,
        buildingsOptions,
        selectedOperatorId,
        selectedWorksiteId,
        selectedApartmentId,
        selectedBuildingId,
        handleOperatorChange,
        handleWorksiteChange,
        handleApartmentChange,
        handleBuildingChange,
        testsheet,
        answerLabelMap
    } = useCreateTestsheetsPageVM();

    const renderCurrentStepContent = () => {
        if (currentStep === 0) {
            return (
                <Container bordered="grey" padding="medium large" className="worksite-selection-container">
                    <Typography intrinsic="h4" className="mb-small-medium-spacing">
                        Sélectionnez le responsable, le chantier et un bâtiment ou un appartement
                    </Typography>
                    <div className="worksite-selection-inputs">
                        <Dropdown
                            options={operatorsOptions}
                            placeholder="Responsable"
                            onOptionChange={(option) => handleOperatorChange(option.id)}
                            value={operatorsOptions.find((operator) => operator.id === selectedOperatorId) || null}
                            disabled={isCreating}
                        />
                        <Dropdown
                            options={worksitesOptions}
                            placeholder="Chantier"
                            onOptionChange={(option) => handleWorksiteChange(option.id)}
                            value={
                                selectedWorksiteId && worksitesOptions.length > 0
                                    ? worksitesOptions.find((worksite) => worksite.id === selectedWorksiteId) || null
                                    : null
                            }
                            disabled={!selectedOperatorId || isCreating}
                            isSearchable={true}
                        />
                        {buildingsOptions.length > 0 && (
                            <Dropdown
                                options={buildingsOptions}
                                placeholder="Bâtiment"
                                onOptionChange={(option) => handleBuildingChange(option.id)}
                                value={
                                    selectedBuildingId && buildingsOptions.length > 0
                                        ? buildingsOptions.find((building) => building.id === selectedBuildingId) || null
                                        : null
                                }
                                disabled={!selectedWorksiteId || isCreating}
                                isSearchable={true}
                            />
                        )}
                        <Dropdown
                            options={apartmentsOptions}
                            placeholder="Appartement"
                            onOptionChange={(option) => handleApartmentChange(option.id)}
                            value={
                                selectedApartmentId && apartmentsOptions.length > 0
                                    ? apartmentsOptions.find((apartment) => apartment.id === selectedApartmentId) || null
                                    : null
                            }
                            disabled={!selectedWorksiteId || isCreating}
                            isSearchable={true}
                        />
                    </div>
                </Container>
            );
        }

        if (currentStep === computedStepperSteps.length - 1) {
            return (
                <Container bordered="grey" padding="medium large" className="observations-container">
                    <Typography intrinsic="h4" className="mb-small-medium-spacing">
                        Observations (facultatif)
                    </Typography>
                    <Textarea
                        placeholder="Ajoutez vos observations..."
                        value={observations}
                        onValueChange={setObservations}
                        disabled={isCreating}
                    />
                </Container>
            );
        }

        const questionStepIndex = currentStep - 1;
        const currentQuestions = form[questionStepIndex]?.questions;

        if (!currentQuestions) return null;

        return (
            <div className="create-testsheets-page-content">
                {currentQuestions.map((question: TestsheetQuestion) => {
                    const selectedAnswerId = selectedAnswers[question.id_question];
                    const selectedAnswer = question.answers.find((a) => a.id_answer === selectedAnswerId);
                    const isNonConforme = selectedAnswer && selectedAnswer.label === 'NC';

                    return (
                        <Container bordered="grey" padding="medium large" key={question.id_question} className="question-container">
                            <Typography intrinsic="h4" className="mb-small-medium-spacing">
                                {question.label}
                            </Typography>
                            <div className="answers-container">
                                {question.answers.map((answer: Answer) => (
                                    <Checkbox
                                        key={answer.id_answer}
                                        label={answerLabelMap[answer.label] || answer.label}
                                        checked={selectedAnswers[question.id_question] === answer.id_answer}
                                        onChange={() => {
                                            selectAnswer(question.id_question, answer.id_answer);
                                        }}
                                        disabled={isCreating}
                                    />
                                ))}
                            </div>
                            {isNonConforme && (
                                <Container padding="none" className="mt-small-medium-spacing">
                                    <Textarea
                                        placeholder="Commentaire (obligatoire pour non conforme)"
                                        value={questionComments[question.id_question] || ''}
                                        onValueChange={(value: string) => setQuestionComment(question.id_question, value)}
                                        disabled={isCreating}
                                    />
                                </Container>
                            )}
                        </Container>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="create-testsheets-page">
            {testsheet && (
                <CustomPageTitle>
                    <Typography intrinsic="h3" iconColor="red" icon={<HiMiniDocumentMagnifyingGlass />} iconFilled iconSize={20}>
                        {testsheet.title}
                    </Typography>
                </CustomPageTitle>
            )}
            <Container className="create-testsheets-page-header" radius="none" column>
                <Typography intrinsic="h3" className="mb-large-spacing">
                    {computedStepperSteps[currentStep]?.label}
                </Typography>
                <MinimalStepper steps={computedStepperSteps} />
            </Container>

            <Container>{renderCurrentStepContent()}</Container>

            <CustomBottomBar>
                <Button
                    color="marine"
                    variant="secondary"
                    onClick={handlePrevStep}
                    disabled={!canGoPrev || isCreating}
                    className="sm-bottom-bar-button mr-small-spacing"
                >
                    <Icon className="arrow-left" color="grey" icon={<LiaArrowLeftSolid />} size={30} />
                </Button>
                <Button
                    variant="primary"
                    color="marine"
                    size="fullwidth"
                    onClick={isLastStep ? handleFinish : handleNextStep}
                    disabled={(!canGoNext && !isLastStep) || isCreating}
                    className="next-button"
                    icon={!isLastStep ? <MdKeyboardArrowRight /> : undefined}
                    iconPosition={!isLastStep ? 'forced-right' : undefined}
                    iconColor={!isLastStep ? 'white' : undefined}
                    iconSize={!isLastStep ? 30 : undefined}
                >
                    <Typography intrinsic="h4">{isCreating ? 'Création...' : isLastStep ? 'Terminer' : 'Suivant'}</Typography>
                </Button>
            </CustomBottomBar>
        </div>
    );
};
